import React, { useState, useEffect, useRef } from "react";
import { database, functions } from "../../firebase"; 
import { ref, onValue, push, set, serverTimestamp, update, remove } from "firebase/database";
import { httpsCallable } from "firebase/functions"; 
import { FaCreditCard, FaUser, FaMoneyBillWave, FaShieldAlt, FaHistory, FaCheckCircle, FaRegEdit, FaSave, FaTrash } from "react-icons/fa";

const AdminPaymentManager = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedBillingKey, setSelectedBillingKey] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [amount, setAmount] = useState("");
  const [orderName, setOrderName] = useState("Google Workspace 정기 서비스 이용료");
  const [charging, setCharging] = useState(false);
  const [history, setHistory] = useState([]); 
  const [refreshingIds, setRefreshingIds] = useState(new Set()); // 다중 로딩 대응을 위해 Set으로 변경
  const hasAutoChecked = useRef(false); // 자동 체크 여부 추적
  const [memos, setMemos] = useState({}); 
  const [searchTerm, setSearchTerm] = useState(""); 
  const [currentMonth, setCurrentMonth] = useState(new Date().toISOString().slice(0, 7)); // YYYY-MM
  const [viewAll, setViewAll] = useState(false); // 전체 조회 상태 추가

  useEffect(() => {
    const usersRef = ref(database, "users");
    const unsubscribe = onValue(usersRef, (snapshot) => {
      const data = snapshot.val();
      if (data) {
        const userList = Object.entries(data).map(([uid, userData]) => {
          const billingKeys = userData.billingKeys 
            ? Object.entries(userData.billingKeys).map(([id, keyData]) => ({ id, ...keyData }))
            : [];
          return { uid, ...userData, billingKeys };
        }).filter(u => u.billingKeys.length > 0); // 빌링키가 있는 사용자만 필터링
        
        setUsers(userList);
      } else {
        setUsers([]);
      }
      setLoading(false);
    });

    // 결제 내역 가져오기
    const historyRef = ref(database, "payment_history");
    const unsubscribeHistory = onValue(historyRef, (snapshot) => {
      const data = snapshot.val();
      if (data) {
        const historyList = Object.entries(data)
          .map(([id, val]) => ({ id, ...val }))
          .sort((a, b) => b.timestamp - a.timestamp); // 최신순 정렬
        setHistory(historyList);
      }
    });

    return () => {
      unsubscribe();
      unsubscribeHistory();
    };
  }, []);

  // [추가] 로딩 시 자동 상태 체크 로직
  useEffect(() => {
    if (!loading && history.length > 0 && !hasAutoChecked.current) {
      hasAutoChecked.current = true;
      // 최근 10건 중 취소되지 않은 건들에 대해 자동 상태 확인 실행
      const itemsToAutoCheck = history
        .slice(0, 10)
        .filter(item => item.status !== 'CANCELLED');
      
      itemsToAutoCheck.forEach(item => {
        refreshStatus(item, true); // true: 알림창 띄우지 않는 silent 모드
      });
    }
  }, [loading, history]);

  // 검색 필터링 로직
  const filteredUsers = users.filter(user => {
    const searchLower = searchTerm.toLowerCase();
    const memo = user.paymentMemo?.toLowerCase() || "";
    return (
      user.name?.toLowerCase().includes(searchLower) ||
      user.email?.toLowerCase().includes(searchLower) ||
      memo.includes(searchLower)
    );
  });

  // 월별 필터링된 히스토리
  const filteredHistory = history.filter(item => {
    if (!item.timestamp) return false;
    if (viewAll) return true; 
    const itemMonth = new Date(item.timestamp).toISOString().slice(0, 7);
    return itemMonth === currentMonth;
  });

  // 해당 월 합계 계산 (결제완료 vs 취소됨 분리)
  const paidTotal = filteredHistory
    .filter(item => item.status === 'PAID' || item.status === 'SUCCESS')
    .reduce((acc, cur) => acc + (cur.amount || 0), 0);
    
  const cancelledTotal = filteredHistory
    .filter(item => item.status === 'CANCELLED')
    .reduce((acc, cur) => acc + (cur.amount || 0), 0);

  const handleCharge = async () => {
    if (!selectedBillingKey || !amount || !orderName) {
      alert("모든 필드를 입력해주세요.");
      return;
    }

    if (!window.confirm(`${selectedUser.name} 고객님께 ${Number(amount).toLocaleString()}원 청구를 진행하시겠습니까?`)) {
      return;
    }

    setCharging(true);
    try {
      const chargeFn = httpsCallable(functions, "callChargeByBillingKey"); // 이름 변경 및 전역 인스턴스 사용

      
      const result = await chargeFn({
        billingKey: selectedBillingKey.billingKey,
        amount: Number(amount), // [필수] 문자열 "1000"을 숫자 1000으로 변환
        orderName: orderName,
        paymentId: `pay_${Date.now()}_${selectedUser.uid.substring(0, 5)}`,
        customerId: selectedUser.uid,
        fullName: selectedUser.name,
        phoneNumber: selectedUser.phone || selectedUser.phoneNumber,
        email: selectedUser.email,
        storeId: process.env.REACT_APP_PORTONE_STORE_ID || "store-354ff911-df8f-4b5e-b534-a10e5d03a4d7", 
        channelKey: process.env.REACT_APP_PORTONE_CHANNEL_KEY || "channel-key-a2166189-e05c-458e-8ea1-da253adb02f3"
      });

      const { data } = result;
      
      if (data.success) {
        // Firebase에 청구 내역 저장
        const newHistoryRef = push(ref(database, "payment_history"));
        const paymentDetails = {
          uid: selectedUser.uid,
          userName: selectedUser.name,
          userEmail: selectedUser.email,
          amount: Number(amount),
          orderName: orderName,
          paymentId: data.paymentId,
          cardName: selectedBillingKey.name,
          cardNumber: selectedBillingKey.number,
          timestamp: serverTimestamp(),
          status: "SUCCESS"
        };
        await set(newHistoryRef, paymentDetails);

        // [추가] 결제 완료 메일 발송
        try {
          const emailUrl = process.env.REACT_APP_EMAIL_API_URL || "https://sendsupportticketemail-hxxheppfsq-uc.a.run.app";
          const emailKey = process.env.REACT_APP_EMAIL_API_KEY || "om8B2QikaJvIImlhlXTw";

          await fetch(
            emailUrl,
            {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
                "X-API-Key": emailKey,
              },
              body: JSON.stringify({
                to: selectedUser.email,
                name: selectedUser.name,
                subject: `[Workway] 결제 완료 안내 - ${orderName}`,
                emailType: "payment",
                amount: Number(amount),
                orderName: orderName,
                cardName: selectedBillingKey.name,
                cardNumber: selectedBillingKey.number,
                paymentId: data.paymentId,
                ticketTitle: orderName // 기존 필수 필드 호환성 유지
              }),
            }
          );
        } catch (emailError) {
          console.error("Receipt email send failed:", emailError);
          // 메일 발송 실패가 결제 프로세스 전체의 실패는 아니므로 알림만 기록
        }

        alert("청구가 성공적으로 완료되었습니다!\n결제 ID: " + data.paymentId + "\n(고객에게 영수증 메일이 발송되었습니다)");
        setSelectedBillingKey(null);
        setAmount("");
      } else {
        alert("청구 실패: " + (data.message || "오류가 발생했습니다."));
      }
    } catch (error) {
      console.error("Charge Error:", error);
      alert(`청구 오류: ${error.message}`);
    } finally {
      setCharging(false);
    }
  };

  // 실시간 상태 업데이트 함수 추가
  const refreshStatus = async (item, silent = false) => {
    setRefreshingIds(prev => new Set(prev).add(item.id));
    try {
      const getInfoFn = httpsCallable(functions, "getPaymentInfo");
      const result = await getInfoFn({ paymentId: item.paymentId });
      const portoneData = result.data.data;

      // DB 상태와 포트원 상태가 다를 때만 업데이트
      if (item.status !== portoneData.status) {
        const statusRef = ref(database, `payment_history/${item.id}`);
        await set(statusRef, {
          ...item,
          status: portoneData.status,
          cancellationReason: portoneData.cancellations?.[0]?.reason || null,
          updatedAt: serverTimestamp()
        });
        if (!silent) alert(`상태가 업데이트되었습니다: ${portoneData.status}`);
      } else {
        if (!silent) alert(`현재 상태(${portoneData.status})가 최신입니다.`);
      }
    } catch (error) {
      console.error("Refresh Status Error:", error);
      if (!silent) alert("상태 업데이트 실패: " + error.message);
    } finally {
      setRefreshingIds(prev => {
        const next = new Set(prev);
        next.delete(item.id);
        return next;
      });
    }
  };

  // 메모 저장 함수
  const handleSaveMemo = async (uid) => {
    const memoValue = memos[uid];
    try {
      const userRef = ref(database, `users/${uid}`);
      await update(userRef, { paymentMemo: memoValue || "" });
      alert("메모가 저장되었습니다.");
    } catch (error) {
      console.error("Memo Save Error:", error);
      alert("메모 저장 실패: " + error.message);
    }
  };

  // [추가] 청구 내역 삭제 함수
  const handleDeleteHistory = async (itemId) => {
    if (!window.confirm("이 청구 내역을 삭제하시겠습니까? (이 작업은 되돌릴 수 없으며 관리 명부에서 영구적으로 삭제됩니다. 실제 결제가 취소되지는 않습니다.)")) {
      return;
    }

    try {
      const itemRef = ref(database, `payment_history/${itemId}`);
      await remove(itemRef);
      alert("내역이 삭제되었습니다.");
    } catch (error) {
      console.error("Delete History Error:", error);
      alert("삭제 실패: " + error.message);
    }
  };

  // [추가] 빌링키(카드) 삭제 함수
  const handleDeleteBillingKey = async (uid, billingKeyId) => {
    if (!window.confirm("사용자의 이 결제 수단(카드)을 삭제하시겠습니까? 삭제 후에는 다시 등록해야 청구가 가능합니다.")) {
      return;
    }

    try {
      const keyRef = ref(database, `users/${uid}/billingKeys/${billingKeyId}`);
      await remove(keyRef);
      alert("결제 수단이 삭제되었습니다.");
    } catch (error) {
      console.error("Delete BillingKey Error:", error);
      alert("삭제 실패: " + error.message);
    }
  };

  if (loading) return <div className="p-8 text-center text-gray-500 text-lg">사용자 정보를 불러오는 중...</div>;

  return (
    <div className="w-full py-8 px-4">
      <div className="mb-8 border-b pb-4">
        <h1 className="text-3xl font-extrabold text-gray-900 flex items-center">
          <FaShieldAlt className="mr-3 text-blue-600" /> 결제 청구 관리 (Admin)
        </h1>
        <p className="text-gray-600 mt-2">등록된 법인카드를 가진 사용자에게 수동으로 이용료를 청구할 수 있습니다.</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* User List Section */}
        <div className="lg:col-span-2 space-y-4">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-4">
            <h2 className="text-xl font-bold text-gray-800 flex items-center">
              <FaUser className="mr-2" /> 청구 대상 사용자 ({filteredUsers.length}명)
            </h2>
            <div className="relative flex-1 max-w-sm">
              <input 
                type="text"
                placeholder="이름, 이메일, 메모로 검색..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-indigo-500 outline-none text-sm transition-all"
              />
              <FaUser className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm" />
            </div>
          </div>
          
          {filteredUsers.length === 0 ? (
            <div className="bg-white p-12 text-center rounded-xl border border-dashed border-gray-300">
               <FaCreditCard className="mx-auto text-4xl text-gray-300 mb-3" />
               <p className="text-gray-500">결제 수단이 등록된 사용자가 없습니다.</p>
            </div>
          ) : (
            <div className="grid gap-4 max-h-[800px] overflow-y-auto pr-2 custom-scrollbar">
              {filteredUsers.map((user) => (
                <div key={user.uid} className="bg-white p-6 rounded-xl shadow-sm border border-gray-100 hover:shadow-md transition-shadow">
                  <div className="flex justify-between items-start mb-4">
                    <div>
                      <h3 className="text-lg font-bold text-gray-900">{user.name}</h3>
                      <p className="text-sm text-gray-500">{user.email}</p>
                    </div>
                    <span className="px-3 py-1 bg-blue-50 text-blue-600 text-xs font-semibold rounded-full uppercase tracking-tighter">Verified</span>
                  </div>

                  {/* 메모 기능 추가 - 정돈된 디자인 */}
                  <div className="mt-4 mb-4 p-4 bg-gray-50 rounded-2xl border border-gray-200 shadow-sm transition-all hover:border-indigo-200 group">
                    <div className="flex items-center justify-between mb-3 border-b border-gray-100 pb-2">
                       <label className="text-[10px] font-black text-gray-400 uppercase flex items-center tracking-widest">
                         <FaRegEdit className="mr-1.5 text-indigo-400" /> Administrative Billing Note
                       </label>
                       <button 
                         onClick={() => handleSaveMemo(user.uid)}
                         className="text-[10px] bg-gray-900 text-white px-4 py-1.5 rounded-xl font-black hover:bg-indigo-600 transition-all flex items-center shadow-lg active:scale-95"
                       >
                         <FaSave className="mr-1.5" /> 저장하기
                       </button>
                    </div>
                    <textarea 
                      value={memos[user.uid] !== undefined ? memos[user.uid] : (user.paymentMemo || "")}
                      onChange={(e) => setMemos({ ...memos, [user.uid]: e.target.value })}
                      placeholder="청구 일정이나 고객 특이사항을 입력하세요..."
                      rows="2"
                      className="w-full bg-transparent border-0 text-sm p-0 focus:ring-0 outline-none font-bold text-gray-800 placeholder-gray-300 resize-none transition-colors"
                    />
                  </div>
                  
                  <div className="space-y-2 mt-4">
                    {user.billingKeys.map((bk) => (
                      <div key={bk.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-200">
                        <div className="flex items-center">
                          <FaCreditCard className="text-blue-500 mr-3 text-xl" />
                          <div>
                            <p className="text-sm font-bold text-gray-800">{bk.name}</p>
                            <p className="text-xs text-gray-500">{bk.number}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <button 
                            onClick={() => {
                              setSelectedUser(user);
                              setSelectedBillingKey(bk);
                            }}
                            className="px-4 py-2 bg-indigo-600 text-white text-sm font-bold rounded-lg hover:bg-indigo-700 transition-colors shadow-sm"
                          >
                            선택하기
                          </button>
                          <button 
                            onClick={() => handleDeleteBillingKey(user.uid, bk.id)}
                            className="p-2 text-gray-400 hover:text-red-500 transition-colors"
                            title="카드 삭제"
                          >
                            <FaTrash />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Action Panel */}
        <div className="lg:col-span-1">
          <div className="bg-white p-8 rounded-2xl shadow-xl border border-gray-100 sticky top-8">
            <h2 className="text-2xl font-black text-gray-900 mb-6 flex items-center uppercase tracking-tight">
              <FaMoneyBillWave className="mr-3 text-green-500" /> 청구 실행
            </h2>

            {selectedBillingKey ? (
              <div className="space-y-6">
                <div className="p-4 bg-gray-50 rounded-xl border border-gray-200">
                  <p className="text-xs text-gray-400 uppercase font-bold mb-1">SELECTED TARGET</p>
                  <p className="text-lg font-bold text-blue-600">{selectedUser.name}</p>
                  <p className="text-sm text-gray-600">{selectedBillingKey.name} ({selectedBillingKey.number})</p>
                  <button 
                    onClick={() => setSelectedBillingKey(null)}
                    className="mt-2 text-xs text-red-500 hover:underline font-bold"
                  >
                    초기화
                  </button>
                </div>

                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-bold text-gray-700 mb-2 uppercase tracking-widest">청구 항목명</label>
                    <input 
                      type="text"
                      value={orderName}
                      onChange={(e) => setOrderName(e.target.value)}
                      className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all outline-none"
                      placeholder="예: 12월 정기 구독료"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-bold text-gray-700 mb-2 uppercase tracking-widest">청구 금액 (KRW)</label>
                    <div className="relative">
                      <input 
                        type="text"
                        value={Number(amount).toLocaleString()}
                        onChange={(e) => {
                          const val = e.target.value.replace(/[^0-9]/g, "");
                          setAmount(val);
                        }}
                        className="w-full pl-10 pr-4 py-4 text-2xl font-black rounded-xl border-2 border-indigo-100 bg-indigo-50 text-indigo-700 focus:border-indigo-500 transition-all outline-none"
                        placeholder="0"
                      />
                      <span className="absolute left-4 top-1/2 -translate-y-1/2 text-indigo-400 text-xl font-black">원</span>
                    </div>
                  </div>
                </div>

                <button 
                  onClick={handleCharge}
                  disabled={charging}
                  className={`w-full py-5 rounded-2xl text-xl font-black text-white shadow-lg transition-all transform active:scale-95 ${
                    charging 
                    ? "bg-gray-400 cursor-not-allowed" 
                    : "bg-gradient-to-br from-indigo-600 to-blue-700 hover:shadow-indigo-200 hover:-translate-y-1"
                  }`}
                >
                  {charging ? "결제 처리 중..." : "즉시 청구하기"}
                </button>
                
                <p className="text-center text-xs text-gray-400 font-medium">
                  청구 시 해당 카드로 즉시 결제가 발생합니다.
                </p>
              </div>
            ) : (
              <div className="py-20 text-center space-y-4">
                <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <FaUser className="text-gray-300 text-2xl" />
                </div>
                <p className="text-gray-400 font-bold">왼쪽 목록에서 사용자를<br/>먼저 선택해주세요.</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* 최근 청구 내역 섹션 */}
      <section className="mt-12 bg-white rounded-2xl shadow-sm border border-gray-100 p-8">
        <div className="flex flex-col md:flex-row md:items-center justify-between mb-8 pb-4 border-b border-gray-100 gap-4">
          <h2 className="text-2xl font-black text-gray-900 flex items-center">
            <FaHistory className="mr-3 text-orange-500" /> 청구 내역 조회
          </h2>
          
          <div className="flex items-center gap-4 bg-gray-50 p-2 rounded-xl">
            <div className="flex items-center bg-white border border-gray-200 rounded-lg p-1">
              <button 
                onClick={() => setViewAll(false)}
                className={`px-3 py-1.5 text-xs font-bold rounded-md transition-all ${!viewAll ? 'bg-indigo-600 text-white shadow-sm' : 'text-gray-400 hover:text-gray-600'}`}
              >
                월별
              </button>
              <button 
                onClick={() => setViewAll(true)}
                className={`px-3 py-1.5 text-xs font-bold rounded-md transition-all ${viewAll ? 'bg-indigo-600 text-white shadow-sm' : 'text-gray-400 hover:text-gray-600'}`}
              >
                전체
              </button>
            </div>
            {!viewAll && (
              <input 
                type="month" 
                value={currentMonth}
                onChange={(e) => setCurrentMonth(e.target.value)}
                className="bg-white border border-gray-200 rounded-lg px-3 py-2 text-sm font-bold text-gray-700 outline-none focus:ring-2 focus:ring-orange-200"
              />
            )}
            <div className="flex gap-2">
              <div className="px-4 py-2 bg-red-50 text-red-600 border border-red-100 rounded-lg text-xs font-black whitespace-nowrap">
                {viewAll ? '누적 취소' : '월 취소'}: {cancelledTotal.toLocaleString()}원
              </div>
              <div className="px-4 py-2 bg-indigo-600 text-white rounded-lg text-sm font-black whitespace-nowrap shadow-sm">
                {viewAll ? '누적 실결제' : '월 실결제'}: {paidTotal.toLocaleString()}원
              </div>
            </div>
          </div>
        </div>

        <div className="overflow-x-auto rounded-xl border border-gray-50">
          <table className="w-full text-left">
            <thead>
              <tr className="text-xs font-black text-gray-400 uppercase tracking-widest border-b border-gray-50 bg-gray-50/50">
                <th className="py-4 px-6">청구 일시</th>
                <th className="py-4 px-6">사용자</th>
                <th className="py-4 px-6">청구 항목</th>
                <th className="py-4 px-6 text-right">금액</th>
                <th className="py-4 px-6">결제 ID</th>
                <th className="py-4 px-6 text-center">상태</th>
                <th className="py-4 px-6 text-center">관리</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {filteredHistory.length === 0 ? (
                <tr>
                  <td colSpan="6" className="py-20 text-center text-gray-400 font-bold">
                    {currentMonth} 내역이 없습니다.
                  </td>
                </tr>
              ) : (
                filteredHistory.map((item) => (
                  <tr key={item.id} className="text-sm hover:bg-gray-50 transition-colors">
                    <td className="py-5 px-6 whitespace-nowrap text-gray-500">
                      {item.timestamp ? new Date(item.timestamp).toLocaleString() : '-'}
                    </td>
                    <td className="py-5 px-6">
                      <div className="font-bold text-gray-900">{item.userName}</div>
                      <div className="text-xs text-gray-500">{item.userEmail}</div>
                    </td>
                    <td className="py-5 px-6">
                      <div className="font-medium text-gray-700">{item.orderName}</div>
                      <div className="text-[10px] text-gray-400 uppercase font-black">{item.cardName} ({item.cardNumber?.substring(item.cardNumber.length - 4)})</div>
                    </td>
                    <td className="py-5 px-6 text-right font-black text-indigo-700 text-lg">
                      {item.amount.toLocaleString()}원
                    </td>
                    <td className="py-5 px-6 font-mono text-[10px] text-gray-300">
                      {item.paymentId}
                    </td>
                    <td className="py-5 px-6 text-center">
                      <div className="flex flex-col items-center gap-1">
                        <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-black uppercase tracking-tighter ${
                          item.status === 'PAID' || item.status === 'SUCCESS' 
                            ? 'bg-green-50 text-green-700' 
                            : item.status === 'CANCELLED'
                            ? 'bg-red-50 text-red-700'
                            : 'bg-orange-50 text-orange-700'
                        }`}>
                          {item.status === 'SUCCESS' || item.status === 'PAID' ? <FaCheckCircle className="mr-1" /> : null}
                          {item.status}
                        </span>
                        <button 
                          onClick={() => refreshStatus(item)}
                          disabled={refreshingIds.has(item.id)}
                          className={`text-[10px] font-bold flex items-center transition-all ${
                            refreshingIds.has(item.id) ? "text-orange-500" : "text-blue-500 hover:text-blue-700"
                          }`}
                        >
                          {refreshingIds.has(item.id) ? (
                            <>
                              <div className="w-2 h-2 border-2 border-orange-500 border-t-transparent rounded-full animate-spin mr-1"></div>
                              상태 체크 중...
                            </>
                          ) : (
                            "실시간 상태 확인"
                          )}
                        </button>
                        {item.cancellationReason && (
                          <div className="text-[10px] text-red-400 mt-1 max-w-[100px] truncate" title={item.cancellationReason}>
                            사유: {item.cancellationReason}
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="py-5 px-6 text-center">
                       <button 
                         onClick={() => handleDeleteHistory(item.id)}
                         className="p-2 text-gray-400 hover:text-red-500 transition-colors"
                         title="내역 삭제"
                       >
                         <FaTrash />
                       </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
};

export default AdminPaymentManager;
